/**
 * NxTurnos — la pestaña "Turnos" de Horarios (PANEL-HORARIOS-TURNOS-T1, API de A: nexo-api 333).
 *
 * Audiencia: Josse o el gerente, en el PC (y que no se rompa en el telefono). Tres bloques:
 *   1. las horas de cada turno (temprano / tarde) por dia, editables en la celda, con "guardar tambien en" otros dias;
 *   2. quien alterna: persona, semana de referencia (se lleva a su lunes) y el turno de esa semana;
 *   3. esta semana y la siguiente de cada uno, con las horas que el sistema va a usar para la tardanza.
 * Despues de guardar se RELEE la API y se muestra lo que quedo (el eco), no lo que se mando.
 * La logica (leer, validar, la paridad de semanas, los mensajes) vive en application/horarios/turnos.js.
 *
 * @module presentation/shared/NxTurnos v1.0
 */
import { HorarioTurnosApiClient } from '../../infrastructure/api/HorarioTurnosApiClient.js';
import { AppConfig } from '../../config.js';
import {
  adaptarTurnos, cuerpoTurno, cuerpoAlternancia, causaDelFallo, semanaDeCadaUno, choques, lineaDelTurno,
  turnoDeLaSemana, quienesPuedenEntrar, lunesDe, fechaISO, TURNOS, NOMBRE_TURNO, DIAS, NOMBRE_DIA
} from '../../application/horarios/turnos.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';

// Estilos acotados a .nx-tu, una sola vez. Los inputs llevan min-width:0 propio: .form-input trae min-width:120px.
function estilos() {
  if (document.getElementById('nx-tu-estilos')) return;
  const s = document.createElement('style'); s.id = 'nx-tu-estilos';
  s.textContent = `.nx-tu{display:flex;flex-direction:column;gap:18px}
.nx-tu :is(.form-input,select,input){font-size:14px}
.nx-tu h4{margin:0 0 4px}.nx-tu-ayuda{font-size:14px;opacity:.75;margin:0 0 8px}
.nx-tu-aviso{padding:8px 12px;border-radius:8px;background:rgba(231,192,122,.12);color:#e7c07a;font-size:14px}
.nx-tu-error{padding:10px 12px;border-radius:8px;background:rgba(255,99,99,.10);color:#ff9b9b}
.nx-tu-grilla{display:grid;grid-template-columns:96px 1fr 1fr;gap:6px}
.nx-tu-hdr{font-size:14px;text-transform:uppercase;opacity:.7;padding:4px 8px}
.nx-tu-dia{padding:10px 8px;font-weight:600;align-self:center}
.nx-tu-celda{min-height:44px;padding:6px 10px;border-radius:8px;background:rgba(255,255,255,.04);display:flex;flex-direction:column;justify-content:center;gap:2px;cursor:pointer}
.nx-tu-alm{font-size:14px;opacity:.7}
.nx-tu-celda:hover{background:rgba(255,255,255,.08)}.nx-tu-vacia{opacity:.55;font-style:italic}
.nx-tu-editor{grid-column:1/-1;padding:10px;border-radius:8px;background:rgba(124,92,255,.10);display:flex;flex-direction:column;gap:8px}
.nx-tu-horas{display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end}
.nx-tu-horas label{display:flex;flex-direction:column;font-size:14px;opacity:.9;gap:2px}
.nx-tu input[type=time],.nx-tu input[type=date],.nx-tu select{min-width:0!important;width:auto;flex:none;height:34px}
.nx-tu-dias{display:flex;flex-wrap:wrap;gap:6px;font-size:14px}.nx-tu-dias label{display:flex;gap:4px;align-items:center}
.nx-tu-botones{display:flex;gap:8px;align-items:center;flex-wrap:wrap}.nx-tu-eco{font-size:14px}
.nx-tu-eco-ok{color:#8fd694}.nx-tu-eco-mal{color:#ff9b9b}
.nx-tu-alt{display:grid;grid-template-columns:minmax(120px,1.2fr) auto auto auto minmax(140px,1fr) auto;gap:8px;align-items:center;min-height:44px;padding:4px 8px;border-radius:8px;background:rgba(255,255,255,.03)}
.nx-tu-alt .nx-tu-eco{grid-column:1/-1}.nx-tu-alt .nx-tu-eco:empty{display:none}
.nx-tu-toca{font-size:14px;opacity:.85}
.nx-tu-semana{width:100%;border-collapse:collapse;font-size:14px}.nx-tu-semana th,.nx-tu-semana td{padding:6px 8px;text-align:left;border-bottom:1px solid rgba(255,255,255,.06);vertical-align:top}
.nx-tu-chip{display:inline-block;padding:1px 8px;border-radius:10px;font-size:14px;font-weight:600}
.nx-tu-chip-temprano{background:rgba(143,214,148,.18);color:#8fd694}.nx-tu-chip-tarde{background:rgba(124,160,255,.18);color:#9db4ff}
.nx-tu-scroll{overflow-x:auto}
@media (max-width:720px){.nx-tu-grilla{grid-template-columns:1fr 1fr}.nx-tu-dia{grid-column:1/-1;padding:6px 0 0}.nx-tu-hdr-dia{display:none}
.nx-tu-alt{grid-template-columns:1fr 1fr}}`;
  document.head.append(s);
}

const chip = (t) => (t ? `<span class="nx-tu-chip nx-tu-chip-${t}">${NOMBRE_TURNO[t]}</span>` : '—');

/**
 * Pinta la pestaña en `el`. `colaboradores`: la lista activa que ya cargo Horarios (para agregar a quien alterna).
 * `hoy`: para la semana en curso (se puede pasar en pruebas).
 */
export async function renderTurnos(el, businessId, { colaboradores = [], hoy = new Date() } = {}) {
  if (!el) return;
  estilos();
  if (!businessId) { el.innerHTML = '<div class="empty-state"><p>Selecciona un negocio primero.</p></div>'; return; }
  el.innerHTML = '<div class="nx-tu"><div class="nx-tu-celda nx-tu-vacia">Cargando los turnos…</div></div>';

  const env = AppConfig?.env || '';
  let vista = adaptarTurnos(await HorarioTurnosApiClient.leer(businessId), { env });
  let editando = null;   // 'temprano|3'
  const ecos = {};       // clave -> {ok, texto}

  async function recargar() { vista = adaptarTurnos(await HorarioTurnosApiClient.leer(businessId), { env }); }

  function pintar() {
    if (vista.estado !== 'ok') { el.innerHTML = `<div class="nx-tu"><div class="nx-tu-error" data-tu-error>${esc(vista.causa)}</div></div>`; return; }
    const filas = semanaDeCadaUno(vista, hoy);
    const aviso = choques(filas);
    el.innerHTML = `<div class="nx-tu">
      <section>
        <h4>Horas de cada turno</h4>
        <p class="nx-tu-ayuda">Las horas que usa el sistema para quien alterna de turno. Toca una celda para cambiarla. Los descansos siguen en Plantillas.</p>
        <div class="nx-tu-grilla" data-tu-grilla>
          <div class="nx-tu-hdr nx-tu-hdr-dia">Día</div>${TURNOS.map((t) => `<div class="nx-tu-hdr">${NOMBRE_TURNO[t]}</div>`).join('')}
          ${DIAS.map((d) => filaDia(d)).join('')}
        </div>
      </section>
      <section>
        <h4>Quién alterna</h4>
        <p class="nx-tu-ayuda">Elige una semana de referencia y el turno que tuvo esa semana: la siguiente le toca el otro, y así. Quien no está aquí sigue con su plantilla.</p>
        <div data-tu-alternancias style="display:flex;flex-direction:column;gap:6px">
          ${vista.alternancias.map((a) => filaAlternancia(a)).join('')}
          ${filaAgregar()}
        </div>
      </section>
      <section>
        <h4>Esta semana (desde el lunes ${esc(lunesDe(fechaISO(hoy)))})</h4>
        ${aviso ? `<div class="nx-tu-aviso" data-tu-choque>${esc(aviso)}</div>` : ''}
        ${filas.length ? `<div class="nx-tu-scroll"><table class="nx-tu-semana" data-tu-semana>
          <thead><tr><th>Persona</th><th>Esta semana</th><th>La próxima</th>${DIAS.map((d) => `<th>${NOMBRE_DIA[d].slice(0, 3)}</th>`).join('')}</tr></thead>
          <tbody>${filas.map((f) => `<tr data-colab="${esc(f.colaboradorId)}"><td>${esc(f.nombre)}${f.nota ? `<div class="nx-tu-toca">${esc(f.nota)}</div>` : ''}</td>
            <td>${chip(f.estaSemana)}</td><td>${chip(f.proximaSemana)}</td>
            ${f.dias.map((x) => `<td>${x.linea ? esc(x.linea.replace(' · almuerzo ', ' / ')) : '<span class="nx-tu-vacia">—</span>'}</td>`).join('')}</tr>`).join('')}</tbody>
        </table></div>` : '<p class="nx-tu-ayuda">Todavía nadie alterna de turno.</p>'}
      </section>
    </div>`;
    cablear();
  }

  function filaDia(d) {
    const celdas = TURNOS.map((t) => {
      const h = vista.turnos[t][d];
      const linea = lineaDelTurno(h);
      const alm = linea && h.horaInicioAlmuerzo ? `<span class="nx-tu-alm">almuerzo ${esc(h.horaInicioAlmuerzo)}–${esc(h.horaFinAlmuerzo)}</span>` : '';
      const eco = ecos[`${t}|${d}`];
      return `<div class="nx-tu-celda" data-tu-celda="${t}|${d}" role="button" tabindex="0">
        <span class="${linea ? '' : 'nx-tu-vacia'}" data-tu-horas>${linea ? `${esc(h.horaEntrada)} – ${esc(h.horaSalida)}` : 'Sin cargar'}</span>${alm}
        ${eco ? `<span class="nx-tu-eco ${eco.ok ? 'nx-tu-eco-ok' : 'nx-tu-eco-mal'}">${esc(eco.texto)}</span>` : ''}</div>`;
    }).join('');
    const editor = editando && Number(editando.split('|')[1]) === d ? editorTurno(editando.split('|')[0], d) : '';
    return `<div class="nx-tu-dia">${NOMBRE_DIA[d]}</div>${celdas}${editor}`;
  }

  function editorTurno(t, d) {
    const h = vista.turnos[t][d] || {};
    return `<div class="nx-tu-editor" data-tu-editor="${t}|${d}">
      <strong>${NOMBRE_TURNO[t]} · ${NOMBRE_DIA[d]}</strong>
      <div class="nx-tu-horas">
        <label>Entrada<input type="time" class="form-input" data-h="horaEntrada" value="${esc(h.horaEntrada || '')}"></label>
        <label>Sale a almorzar<input type="time" class="form-input" data-h="horaInicioAlmuerzo" value="${esc(h.horaInicioAlmuerzo || '')}"></label>
        <label>Vuelve del almuerzo<input type="time" class="form-input" data-h="horaFinAlmuerzo" value="${esc(h.horaFinAlmuerzo || '')}"></label>
        <label>Salida<input type="time" class="form-input" data-h="horaSalida" value="${esc(h.horaSalida || '')}"></label>
      </div>
      <div class="nx-tu-dias">Guardar también en:
        ${DIAS.filter((x) => x !== d).map((x) => `<label><input type="checkbox" data-otro-dia="${x}">${NOMBRE_DIA[x].slice(0, 3)}</label>`).join('')}
      </div>
      <div class="nx-tu-botones"><button class="btn btn-primary btn-sm" data-tu-guardar>Guardar</button>
        <button class="btn btn-ghost btn-sm" data-tu-cancelar>Cancelar</button><span class="nx-tu-eco" data-tu-msg></span></div>
    </div>`;
  }

  function filaAlternancia(a) {
    const esta = turnoDeLaSemana(a, lunesDe(fechaISO(hoy)));
    const eco = ecos[`alt|${a.colaboradorId}`];
    return `<div class="nx-tu-alt" data-tu-alt="${esc(a.colaboradorId)}">
      <strong>${esc(a.nombre)}</strong>
      <label class="nx-tu-toca">Semana de <input type="date" class="form-input" data-a="semana" value="${esc(a.semanaAncla)}"></label>
      <select class="form-input" data-a="turnoAncla">${TURNOS.map((t) => `<option value="${t}" ${t === a.turnoAncla ? 'selected' : ''}>${NOMBRE_TURNO[t]}</option>`).join('')}</select>
      <label class="nx-tu-toca"><input type="checkbox" data-a="activo" ${a.activo ? 'checked' : ''}> Alterna</label>
      <span class="nx-tu-toca">Esta semana: ${chip(esta)}</span>
      <button class="btn btn-primary btn-sm" data-tu-guardar-alt>Guardar</button>
      <span class="nx-tu-eco ${eco ? (eco.ok ? 'nx-tu-eco-ok' : 'nx-tu-eco-mal') : ''}">${eco ? esc(eco.texto) : ''}</span>
    </div>`;
  }

  function filaAgregar() {
    const libres = quienesPuedenEntrar(colaboradores, vista.alternancias);
    if (!libres.length) return '';
    const eco = ecos['alt|nuevo'];
    return `<div class="nx-tu-alt" data-tu-alt="">
      <select class="form-input" data-a="colaboradorId"><option value="">Agregar persona…</option>
        ${libres.map((c) => `<option value="${esc(c.id)}">${esc(c.nombre)}</option>`).join('')}</select>
      <label class="nx-tu-toca">Semana de <input type="date" class="form-input" data-a="semana" value="${esc(lunesDe(fechaISO(hoy)))}"></label>
      <select class="form-input" data-a="turnoAncla">${TURNOS.map((t) => `<option value="${t}">${NOMBRE_TURNO[t]}</option>`).join('')}</select>
      <label class="nx-tu-toca"><input type="checkbox" data-a="activo" checked> Alterna</label>
      <span></span>
      <button class="btn btn-primary btn-sm" data-tu-guardar-alt>Agregar</button>
      <span class="nx-tu-eco ${eco ? (eco.ok ? 'nx-tu-eco-ok' : 'nx-tu-eco-mal') : ''}">${eco ? esc(eco.texto) : ''}</span>
    </div>`;
  }

  function cablear() {
    el.querySelectorAll('[data-tu-celda]').forEach((c) => {
      const abrir = () => { editando = editando === c.dataset.tuCelda ? null : c.dataset.tuCelda; pintar(); el.querySelector('[data-tu-editor] input')?.focus(); };
      c.addEventListener('click', abrir);
      c.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(); } });
    });
    const ed = el.querySelector('[data-tu-editor]');
    if (ed) {
      ed.querySelector('[data-tu-cancelar]').onclick = () => { editando = null; pintar(); };
      ed.querySelector('[data-tu-guardar]').onclick = () => guardarTurno(ed);
    }
    el.querySelectorAll('[data-tu-alt]').forEach((fila) => {
      fila.querySelector('[data-tu-guardar-alt]').onclick = () => guardarAlternancia(fila);
    });
  }

  async function guardarTurno(ed) {
    const [t, d] = ed.dataset.tuEditor.split('|');
    const horas = {};
    ed.querySelectorAll('[data-h]').forEach((i) => { horas[i.dataset.h] = i.value; });
    const dias = [Number(d), ...[...ed.querySelectorAll('[data-otro-dia]:checked')].map((x) => Number(x.dataset.otroDia))];
    const msg = ed.querySelector('[data-tu-msg]');
    const cuerpos = dias.map((dia) => cuerpoTurno({ turno: t, diaSemana: dia, ...horas }));
    const malo = cuerpos.find((c) => c.error);
    if (malo) {
      msg.className = 'nx-tu-eco nx-tu-eco-mal'; msg.textContent = malo.error;
      ed.querySelector(`[data-h="${malo.campo}"]`)?.focus();
      return;
    }
    const boton = ed.querySelector('[data-tu-guardar]'); boton.disabled = true; msg.className = 'nx-tu-eco'; msg.textContent = 'Guardando…';
    const fallos = [];
    for (const c of cuerpos) {
      const r = await HorarioTurnosApiClient.guardarTurno(businessId, c.cuerpo);
      if (!r || r.ok !== true) fallos.push({ dia: c.cuerpo.diaSemana, causa: causaDelFallo(r, { env }) });
    }
    await recargar();
    // El eco: lo que QUEDO en la API para cada dia, comparado con lo que se mando.
    for (const c of cuerpos) {
      const f = fallos.find((x) => x.dia === c.cuerpo.diaSemana);
      const quedo = vista.estado === 'ok' ? vista.turnos[t][c.cuerpo.diaSemana] : null;
      const igual = quedo && lineaDelTurno(quedo) === lineaDelTurno({ ...c.cuerpo, horaInicioAlmuerzo: c.cuerpo.horaInicioAlmuerzo || '', horaFinAlmuerzo: c.cuerpo.horaFinAlmuerzo || '' });
      ecos[`${t}|${c.cuerpo.diaSemana}`] = f ? { ok: false, texto: f.causa } : igual ? { ok: true, texto: 'Guardado' } : { ok: false, texto: 'La API no devolvió lo que se mandó' };
    }
    editando = fallos.length ? editando : null;
    pintar();
  }

  async function guardarAlternancia(fila) {
    const v = (k) => fila.querySelector(`[data-a="${k}"]`);
    const colaboradorId = fila.dataset.tuAlt || v('colaboradorId')?.value || '';
    const clave = `alt|${fila.dataset.tuAlt || 'nuevo'}`;
    const c = cuerpoAlternancia({ colaboradorId, semana: v('semana').value, turnoAncla: v('turnoAncla').value, activo: v('activo').checked });
    const eco = fila.querySelector('.nx-tu-eco:last-child');
    if (c.error) { eco.className = 'nx-tu-eco nx-tu-eco-mal'; eco.textContent = c.error; return; }
    fila.querySelector('[data-tu-guardar-alt]').disabled = true; eco.className = 'nx-tu-eco'; eco.textContent = 'Guardando…';
    const r = await HorarioTurnosApiClient.guardarAlternancia(businessId, c.cuerpo);
    await recargar();
    if (!r || r.ok !== true) { ecos[clave] = { ok: false, texto: causaDelFallo(r, { env }) }; pintar(); return; }
    const quedo = vista.estado === 'ok' ? vista.alternancias.find((a) => a.colaboradorId === c.cuerpo.colaboradorId) : null;
    const igual = quedo && quedo.semanaAncla === c.cuerpo.semanaAncla && quedo.turnoAncla === c.cuerpo.turnoAncla && quedo.activo === c.cuerpo.activo;
    delete ecos['alt|nuevo'];
    ecos[`alt|${c.cuerpo.colaboradorId}`] = igual
      ? { ok: true, texto: `Guardado: semana del ${c.cuerpo.semanaAncla} en ${NOMBRE_TURNO[c.cuerpo.turnoAncla].toLowerCase()}` }
      : { ok: false, texto: 'La API no devolvió lo que se mandó' };
    pintar();
  }

  pintar();
}
